import { reportError } from '@ms/yammer-telemetry';
import createCachedSelector from 're-reselect';

import { UserEventPermissions } from '../../state/liveEvents/types';
import type {
  LiveEvent as Event,
  LiveEventStatusInformation as EventStatusInformation,
  LiveEventWithStatus as EventWithStatus,
  LiveEvents as Events,
  Group,
  User,
  YammerState,
} from '../../state/types';
import { isFeatureRolloutEnabled } from '../featureRollouts/selectors';
import {
  findLiveEventStatusByBroadcastId,
  findLiveEventStatusByBroadcastIdThrows,
  getLiveEventsStatuses as getEventsStatuses,
} from '../liveEventsStatuses/selectors';
import { getCurrentSessionUser } from '../session/selectors';
import { getUserConnectedLeadersListIdsFromState } from '../userConnectedLeadersList/selectors';
import { getUserFollowedLeadersListIdsFromState } from '../userFollowedLeadersList/selectors';
import { getViewerCommunityIds } from '../viewerCommunityIdSets/selectors';
export * from './eventPublisherSelectors';
type GetLiveEventsWithoutStatus = (state: YammerState) => Events;
const getLiveEventsWithoutStatus: GetLiveEventsWithoutStatus = (state) => state.liveEvents;
const isEventLiveInGroup = (event: EventWithStatus | EventStatusInformation, groupId: Group['id']) =>
  event.groupId === groupId && event.status === 'STARTED';
type FindLiveEventWithoutStatusByBroadcastId = (
  state: YammerState,
  broadcastId: Event['broadcastId']
) => Event | undefined;
/**
 * @deprecated PERFORMANCE This selector returns an entity object and will force re-renders when the entity changes.
 * This should be replaced by a `getLiveEventField` selector.
 */
export const findLiveEventWithoutStatusByBroadcastId: FindLiveEventWithoutStatusByBroadcastId = (state, broadcastId) =>
  state.liveEvents[broadcastId];
type FindLiveEventWithoutStatusByBroadcastIdThrows = (state: YammerState, broadcastId: Event['broadcastId']) => Event;
export const findLiveEventWithoutStatusByBroadcastIdThrows: FindLiveEventWithoutStatusByBroadcastIdThrows = (
  state,
  broadcastId
) => {
  const liveEventWithoutStatus = findLiveEventWithoutStatusByBroadcastId(state, broadcastId);
  if (!liveEventWithoutStatus) {
    throw new Error('Can not find live event without status in the store');
  }

  return liveEventWithoutStatus;
};
type FindLiveEventByBroadcastIdThrows = (state: YammerState, broadcastId: Event['broadcastId']) => EventWithStatus;
/**
 * @deprecated PERFORMANCE This returns an object created from multiple entities and will force re-renders when any
 * sub-entity changes. Selectors should do the opposite, only return individual primitive fields whenever possible
 * with consuming components calling one selector perf field it requires.
 */
export const findLiveEventByBroadcastIdThrows: FindLiveEventByBroadcastIdThrows = createCachedSelector(
  findLiveEventWithoutStatusByBroadcastIdThrows,
  findLiveEventStatusByBroadcastIdThrows,
  (liveEvent: Event, liveEventStatusInformation: EventStatusInformation) => ({
    ...liveEvent,
    ...liveEventStatusInformation,
  })
)((_state, broadcastId) => broadcastId);
type FindLiveEventByBroadcastId = (
  state: YammerState,
  broadcastId: Event['broadcastId']
) => EventWithStatus | undefined;
export const findLiveEventByBroadcastId: FindLiveEventByBroadcastId = createCachedSelector(
  findLiveEventWithoutStatusByBroadcastId,
  findLiveEventStatusByBroadcastId,
  (liveEvent: Event, liveEventStatusInformation: EventStatusInformation) => {
    if (!liveEvent || !liveEventStatusInformation) {
      return undefined;
    }

    return {
      ...liveEvent,
      ...liveEventStatusInformation,
    };
  }
)((_state, broadcastId) => broadcastId);
type GetAllLiveEventsThrows = (state: YammerState) => Record<Event['broadcastId'], EventWithStatus>;
/**
 * @deprecated PERFORMANCE This returns an object of objects created from multiple entities and will force re-renders when any
 * sub-entity in the array changes. The top-level consumer should only select a memoized array of ids, then pass each id to a
 * child component which can select the specific fields it requires and manage its own memoization to avoid re-renders.
 */
export const getAllLiveEventsThrows: GetAllLiveEventsThrows = createCachedSelector(
  getLiveEventsWithoutStatus,
  getEventsStatuses,
  (eventsWithoutStatus, eventsStatuses) =>
    Object.keys(eventsWithoutStatus).reduce<Record<Event['broadcastId'], EventWithStatus>>(
      (eventsWithStatus, eventId) => {
        const eventStatus = eventsStatuses[eventId];
        if (!eventStatus) {
          throw new Error(`Can not find the matching event status for event: ${eventId}`);
        }
        eventsWithStatus[eventId] = {
          ...eventsWithoutStatus[eventId],
          ...eventStatus,
        };

        return eventsWithStatus;
      },
      {}
    )
)(() => 'cache');
type GetAllLiveEvents = (state: YammerState) => Record<Event['broadcastId'], EventWithStatus>;
/**
 * @deprecated PERFORMANCE This returns an object of objects created from multiple entities and will force re-renders when any
 * sub-entity in the array changes. The top-level consumer should only select a memoized array of ids, then pass each id to a
 * child component which can select the specific fields it requires and manage its own memoization to avoid re-renders.
 */
export const getAllLiveEvents: GetAllLiveEvents = createCachedSelector(
  getLiveEventsWithoutStatus,
  getEventsStatuses,
  (eventsWithoutStatus, eventsStatuses) =>
    Object.keys(eventsWithoutStatus).reduce<Record<Event['broadcastId'], EventWithStatus>>(
      (eventsWithStatus, eventId) => {
        const eventStatus = eventsStatuses[eventId];
        if (!eventStatus) {
          const error = new Error(`Can not find the matching event status for event: ${eventId}`);
          reportError({ error });

          return eventsWithStatus;
        }
        eventsWithStatus[eventId] = {
          ...eventsWithoutStatus[eventId],
          ...eventStatus,
        };

        return eventsWithStatus;
      },
      {}
    )
)(() => 'cache');
type FindLiveEventWithoutStatusByEventIdThrows = (state: YammerState, eventId: Event['id']) => Event;
/**
 * @deprecated PERFORMANCE This selector returns an entity object and will force re-renders when the entity changes.
 * This should be replaced by a `findLiveEventField` selector.
 */
export const findLiveEventWithoutStatusByEventIdThrows: FindLiveEventWithoutStatusByEventIdThrows =
  createCachedSelector(
    (_state: YammerState, eventId: Event['id']) => eventId,
    getLiveEventsWithoutStatus,
    (eventId, liveEventsWithoutStatus) => {
      const event = Object.values(liveEventsWithoutStatus).find(
        (liveEventWithoutStatus) => eventId === liveEventWithoutStatus.id
      );
      if (!event) {
        throw new Error(`Could not find event with id ${eventId}`);
      }

      return event;
    }
  )((_state, eventId) => eventId);
type GetLiveEventsInGroup = (state: YammerState, groupId: Group['id']) => EventWithStatus[];
/**
 * @deprecated PERFORMANCE This returns an array of objects created from multiple entities, and will force
 * re-renders in all consuming components whenever any live event changes. This should be replaced by a selector
 * which returns a memoized array of live event IDs for the group, and the top-level consuming component would
 * pass each groupId to a child component. Each child component in that list can use field selectors to select
 * only the specific fields it requires, and manage its own memoization to avoid re-renders.
 */
export const getLiveEventsInGroup: GetLiveEventsInGroup = createCachedSelector(
  (_state: YammerState, groupId: Group['id']) => groupId,
  (state) => getAllLiveEvents(state),
  (groupId, allEvents) =>
    Object.keys(allEvents).reduce<EventWithStatus[]>((liveEvents, eventId) => {
      const event = allEvents[eventId];
      if (isEventLiveInGroup(event, groupId) && event.isDiscoverable) {
        return [...liveEvents, event];
      }

      return liveEvents;
    }, [])
)((_state, groupId) => groupId);
type GetLiveEventsInJoinedGroups = (state: YammerState) => EventWithStatus[];
/**
 * @deprecated PERFORMANCE This returns an array of objects created from multiple entities and will force re-renders when any
 * sub-entity in the array changes, or if ANY fulfilled live event in the store changes due to the getAllLiveEvents sub-selector.
 * The top-level consumer should only select a memoized array of ids, then pass each id to a child component which can select the
 * specific fields it requires and manage its own memoization to avoid re-renders.
 */
export const getLiveEventsInJoinedGroups: GetLiveEventsInJoinedGroups = createCachedSelector(
  getViewerCommunityIds,
  getAllLiveEvents,
  (groupIds, allEvents) =>
    Object.keys(allEvents).reduce<EventWithStatus[]>((liveEvents, eventId) => {
      const event = allEvents[eventId];
      const isEventLiveInJoinedGroups = groupIds.includes(event.groupId) && event.status === 'STARTED';
      if (isEventLiveInJoinedGroups && event.isDiscoverable) {
        return [...liveEvents, event];
      }

      return liveEvents;
    }, [])
)(() => 'cache');
type GetFollowedAndConnectedLeaderIds = (state: YammerState) => User['id'][];
const getFollowedAndConnectedLeaderIds: GetFollowedAndConnectedLeaderIds = createCachedSelector(
  getUserConnectedLeadersListIdsFromState,
  getUserFollowedLeadersListIdsFromState,
  (connectedLeaderIds, followedLeaderIds) => [...connectedLeaderIds, ...followedLeaderIds]
)(() => 'cache');
type GetLiveEventsFromLeadership = (state: YammerState) => EventWithStatus[];
/**
 * @deprecated PERFORMANCE This returns an array of objects created from multiple entities and will force re-renders when any
 * sub-entity in the array changes. The top-level consumer should only select a memoized array of ids, then pass each id to a
 * child component which can select the specific fields it requires and manage its own memoization to avoid re-renders.
 */
export const getLiveEventsFromLeadership: GetLiveEventsFromLeadership = createCachedSelector(
  getAllLiveEvents,
  getFollowedAndConnectedLeaderIds,
  (allEvents, leaderIds) =>
    Object.keys(allEvents).reduce<EventWithStatus[]>((liveEvents, eventId) => {
      const event = allEvents[eventId];
      if (isLiveEventHostedByLeadership(leaderIds, event) && event.isDiscoverable) {
        return [...liveEvents, event];
      }

      return liveEvents;
    }, [])
)(() => 'cache');
type IsLiveEventHostedByLeadership = (leaderIds: User['id'][], event: EventWithStatus) => boolean;
const isLiveEventHostedByLeadership: IsLiveEventHostedByLeadership = (leaderIds, event) => {
  const { organizerId, presenterIds, producerIds } = event;
  const adminIds = [organizerId, ...presenterIds, ...producerIds];

  return leaderIds.some((leaderId) => adminIds.includes(leaderId)) && event.status === 'STARTED';
};
type GetLiveEventsInNetwork = (state: YammerState) => EventWithStatus[];
/**
 * @deprecated PERFORMANCE This returns an array of objects created from multiple entities and will force re-renders when any
 * sub-entity in the array changes. The top-level consumer should only select a memoized array of ids, then pass each id to a
 * child component which can select the specific fields it requires and manage its own memoization to avoid re-renders.
 */
export const getLiveEventsInNetwork: GetLiveEventsInNetwork = createCachedSelector(getAllLiveEvents, (allEvents) =>
  Object.keys(allEvents).reduce<EventWithStatus[]>((liveEvents, eventId) => {
    const event = allEvents[eventId];
    if (event.status === 'STARTED' && event.isDiscoverable) {
      return [...liveEvents, event];
    }

    return liveEvents;
  }, [])
)(() => 'cache');
type GroupHasLiveEvents = (state: YammerState, groupId: Group['id']) => boolean;
export const groupHasLiveEvents: GroupHasLiveEvents = (state, groupId) => {
  const eventsStatuses = getEventsStatuses(state);
  const broadcastIds = Object.keys(eventsStatuses);

  return broadcastIds.some((broadcastId) => {
    const eventStatus = eventsStatuses[broadcastId];

    return isEventLiveInGroup(eventStatus, groupId);
  });
};
type IsCurrentUserEventAdminThrows = (state: YammerState, eventId: Event['broadcastId']) => boolean;
export const isCurrentUserEventAdminThrows: IsCurrentUserEventAdminThrows = (state, eventId) => {
  const { organizerId, presenterIds, producerIds } = findLiveEventWithoutStatusByBroadcastIdThrows(state, eventId);
  const { id: userId } = getCurrentSessionUser(state);
  const adminIds = [organizerId, ...presenterIds, ...producerIds];

  return adminIds.includes(userId);
};
type IsCurrentUserEventAdmin = (state: YammerState, broadcastId: Event['broadcastId']) => boolean;
export const isCurrentUserEventAdmin: IsCurrentUserEventAdmin = (state, broadcastId) => {
  const event = findLiveEventWithoutStatusByBroadcastId(state, broadcastId);
  if (!event) {
    return false;
  }
  const { organizerId, presenterIds, producerIds } = event;
  const { id: userId } = getCurrentSessionUser(state);
  const adminIds = [organizerId, ...presenterIds, ...producerIds];

  return adminIds.includes(userId);
};
type IsCurrentUserAttendeeThrows = (state: YammerState, broadcastId: Event['broadcastId']) => boolean;
export const isCurrentUserAttendeeThrows: IsCurrentUserAttendeeThrows = (state, broadcastId) =>
  !isCurrentUserEventAdminThrows(state, broadcastId);
type IsBYOEEventProducibleByCurrentUser = (state: YammerState, eventId: Event['broadcastId']) => boolean;
export const isBYOEEventProducibleByCurrentUserThrows: IsBYOEEventProducibleByCurrentUser = (state, eventId) => {
  const { eventType } = findLiveEventWithoutStatusByBroadcastIdThrows(state, eventId);
  const isAdmin = isCurrentUserEventAdminThrows(state, eventId);

  return eventType === 'YAMMER_BYOE' && isAdmin;
};
type IsCurrentUserEventOrganizerThrows = (state: YammerState, eventId: Event['broadcastId']) => boolean;
export const isCurrentUserEventOrganizerThrows: IsCurrentUserEventOrganizerThrows = (state, eventId) => {
  const { organizerId } = findLiveEventWithoutStatusByBroadcastIdThrows(state, eventId);
  const { id: userId } = getCurrentSessionUser(state);

  return organizerId === userId;
};
type IsCurrentUserEventProducerThrows = (state: YammerState, eventId: Event['broadcastId']) => boolean;
export const isCurrentUserEventProducerThrows: IsCurrentUserEventProducerThrows = (state, eventId) => {
  const { organizerId, producerIds } = findLiveEventWithoutStatusByBroadcastIdThrows(state, eventId);
  const { id: userId } = getCurrentSessionUser(state);
  const producers = [organizerId, ...producerIds];

  return producers.includes(userId);
};
type IsCurrentUserEventPresenterThrows = (state: YammerState, eventId: Event['broadcastId']) => boolean;
export const isCurrentUserEventPresenterThrows: IsCurrentUserEventPresenterThrows = (state, eventId) => {
  const { presenterIds } = findLiveEventWithoutStatusByBroadcastIdThrows(state, eventId);
  const { id: userId } = getCurrentSessionUser(state);

  return presenterIds.includes(userId);
};
type GetEventPermissionsForCurrentUserThrows = (
  state: YammerState,
  eventId: Event['broadcastId']
) => UserEventPermissions;
export const getEventPermissionsForCurrentUserThrows: GetEventPermissionsForCurrentUserThrows = (state, eventId) => {
  const { eventType, teamsLiveEventType } = findLiveEventWithoutStatusByBroadcastIdThrows(state, eventId);
  if (teamsLiveEventType) {
    const isTeamsEventProducer = isCurrentUserEventProducerThrows(state, eventId);
    const isTeamsEventPresenter = isCurrentUserEventPresenterThrows(state, eventId);
    if (isTeamsEventProducer) {
      return teamsLiveEventType === 'TEAMS_BYOE'
        ? UserEventPermissions.TeamsBYOEProducible
        : UserEventPermissions.TeamsProducible;
    }

    if (isTeamsEventPresenter) {
      return teamsLiveEventType === 'TEAMS_BYOE'
        ? UserEventPermissions.TeamsBYOEPresentable
        : UserEventPermissions.TeamsPresentable;
    }
  } else {
    const isByoeProducible = isBYOEEventProducibleByCurrentUserThrows(state, eventId);
    const isQuickstartEventProducer =
      eventType === 'YAMMER_QUICKSTART' && isCurrentUserEventProducerThrows(state, eventId);
    const isQuickstartEventPresenter =
      eventType === 'YAMMER_QUICKSTART' && isCurrentUserEventPresenterThrows(state, eventId);
    if (isByoeProducible) {
      return UserEventPermissions.BYOEProducible;
    }

    if (isQuickstartEventProducer) {
      return UserEventPermissions.QuickstartProducible;
    }

    if (isQuickstartEventPresenter) {
      return UserEventPermissions.QuickstartPresentable;
    }
  }

  return UserEventPermissions.None;
};
type PastEventIsCancellableByCurrentUserThrows = (state: YammerState, broadcastId: Event['broadcastId']) => boolean;
export const pastEventIsCancellableByCurrentUserThrows: PastEventIsCancellableByCurrentUserThrows = (
  state,
  broadcastId
) => {
  const { status } = findLiveEventByBroadcastIdThrows(state, broadcastId);
  const isOrganizer = isCurrentUserEventOrganizerThrows(state, broadcastId);
  const hasEventEnded = status === 'ENDED';

  return hasEventEnded && isOrganizer;
};
type EventIsEditableByCurrentUserThrows = (state: YammerState, broadcastId: Event['broadcastId']) => boolean;
export const eventIsEditableByCurrentUserThrows: EventIsEditableByCurrentUserThrows = (state, broadcastId) => {
  const { status } = findLiveEventByBroadcastIdThrows(state, broadcastId);
  const isOrganizer = isCurrentUserEventOrganizerThrows(state, broadcastId);
  const hasEventEnded = status === 'ENDED';

  return !hasEventEnded && isOrganizer;
};
type RestrictedPostingIsEditableByCurrentUserThrows = (
  state: YammerState,
  broadcastId: Event['broadcastId']
) => boolean;
export const restrictedPostingIsEditableByCurrentUserThrows: RestrictedPostingIsEditableByCurrentUserThrows = (
  state,
  broadcastId
) => {
  const { status } = findLiveEventByBroadcastIdThrows(state, broadcastId);
  const isRestrictedThreadStarterFeatureEnabled = isFeatureRolloutEnabled(state, 'RestrictThreadStarterInEventFeed');
  const isOrganizer = isCurrentUserEventOrganizerThrows(state, broadcastId);
  const isProducer = isCurrentUserEventProducerThrows(state, broadcastId);
  const hasEventEnded = status === 'ENDED';
  const isOrganizerOfEndedEvent = isOrganizer && hasEventEnded;
  const isOnlyProducer = isProducer && !isOrganizer;
  const isProducerOrOrganizerOfEndedEvent = isOnlyProducer || isOrganizerOfEndedEvent;

  return isRestrictedThreadStarterFeatureEnabled && isProducerOrOrganizerOfEndedEvent;
};
type CanCurrentUserSendGroupInviteForEventThrows = (state: YammerState, broadcastId: Event['broadcastId']) => boolean;
export const canCurrentUserSendGroupInviteForEventThrows: CanCurrentUserSendGroupInviteForEventThrows = (
  state,
  broadcastId
) => {
  const isOrganizer = isCurrentUserEventOrganizerThrows(state, broadcastId);
  const { status } = findLiveEventByBroadcastIdThrows(state, broadcastId);
  const isCreateGroupCalendarInviteEnabled = isFeatureRolloutEnabled(state, 'CreateGroupCalendarInviteForBroadcast');
  const hasEventEnded = status === 'ENDED';

  return isCreateGroupCalendarInviteEnabled && isOrganizer && !hasEventEnded;
};
type GetEventIdThrows = (state: YammerState, broadcastId: Event['broadcastId']) => Event['id'];
export const getEventIdThrows: GetEventIdThrows = (state, broadcastId) =>
  findLiveEventWithoutStatusByBroadcastIdThrows(state, broadcastId).id;
type GetIsEmbeddableThrows = (state: YammerState, broadcastId: Event['broadcastId']) => Event['isEmbeddable'];
export const getIsEmbeddableThrows: GetIsEmbeddableThrows = (state, broadcastId) =>
  findLiveEventWithoutStatusByBroadcastIdThrows(state, broadcastId).isEmbeddable;
type GetIsThreadStarterRestrictedThrows = (
  state: YammerState,
  eventId: Event['id']
) => Event['isThreadStarterRestricted'];
export const getIsThreadStarterRestrictedThrows: GetIsThreadStarterRestrictedThrows = (state, eventId) =>
  findLiveEventWithoutStatusByEventIdThrows(state, eventId).isThreadStarterRestricted;
type IsCurrentUserAttendeeByEventIdThrows = (state: YammerState, eventId: Event['id']) => boolean;
export const isCurrentUserAttendeeByEventIdThrows: IsCurrentUserAttendeeByEventIdThrows = (state, eventId) => {
  const broadcastId = findLiveEventWithoutStatusByEventIdThrows(state, eventId).broadcastId;

  return isCurrentUserAttendeeThrows(state, broadcastId);
};
type EventSidebarHasContentThrows = (state: YammerState, broadcastId: Event['broadcastId']) => boolean;
export const eventSidebarHasContentThrows: EventSidebarHasContentThrows = (state, broadcastId) => {
  const { description } = findLiveEventWithoutStatusByBroadcastIdThrows(state, broadcastId);
  const isAdmin = isCurrentUserEventAdminThrows(state, broadcastId);
  if (isAdmin) {
    return true;
  }

  return !!description;
};
type GetTeamsLiveEventTypeThrows = (
  state: YammerState,
  broadcastId: Event['broadcastId']
) => Event['teamsLiveEventType'];
export const getTeamsLiveEventTypeThrows: GetTeamsLiveEventTypeThrows = (state, broadcastId) =>
  findLiveEventWithoutStatusByBroadcastIdThrows(state, broadcastId).teamsLiveEventType;
